/**
 * Подробная часть карточки цели (настройка владельца card_full, дефолт ВКЛ;
 * референс advanced-3d (8) TenantDrawer/BuildingDrawer):
 * — здание: этажность/код, описание, арендаторы по этажам (клик — их карточка);
 * — арендатор: здание·этаж·помещение·площадь, теги, «как найти внутри»
 *   (attrs.navigation_hint из редактора). «✎» ведёт в редактор (вход по логину).
 */

import { tr } from '@app/i18n'
import { buildingFloors, type FloorItem, floorOccupants, floorOfLevel, type Venue } from '@app/rvf'
import { selectTarget } from '../actions'
import { markerVisual } from '../map/marker-icons'
import { canEditVenue, floorStackFloor, floorStackUnitId, venue, volume3dUnitId } from '../state'
import { volumeTier } from '../volume3d/capability'
import { prefetchVolume3d } from '../volume3d/prefetch'
import { GLYPH_COMPASS } from './glyphs'
import { categoryLabel, featureName } from './labels'

type Unit = Venue['units'][number]
type Poi = Venue['pois'][number]

function pointInRing(pt: readonly number[], ring: readonly number[][]): boolean {
  const x = pt[0] as number
  const y = pt[1] as number
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]?.[0] as number
    const yi = ring[i]?.[1] as number
    const xj = ring[j]?.[0] as number
    const yj = ring[j]?.[1] as number
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

function unitContaining(v: Venue, poi: Poi): Unit | null {
  for (const u of v.units) {
    if (u.level !== poi.level) continue
    const ring = u.geometry.coordinates[0]
    if (ring && pointInRing(poi.point, ring)) return u
  }
  return null
}

/**
 * Этажность здания. Считается общим правилом (floor-occupants): attrs.floors,
 * иначе число уровней площадки. 0 — здание одноэтажное и этажерка не нужна.
 */
export function buildingFloorsOf(v: Venue, unitId: string): number {
  const unit = v.units.find((u) => u.id === unitId)
  if (!unit) return 0
  const floors = buildingFloors(v, unit)
  return floors > 1 ? floors : 0
}

/** Подпись этажа для строки арендатора: номер этажа по уровню объекта. */
function floorLabelOf(v: Venue, item: FloorItem): string {
  const own = item.attrs?.['floor']
  if (typeof own === 'number' || typeof own === 'string') return String(own)
  const poi = v.pois.find((p) => p.id === item.id)
  const unit = v.units.find((u) => u.id === item.id)
  const level = poi?.level ?? unit?.level
  return level ? String(floorOfLevel(v, level)) : ''
}

/** Все арендаторы здания по всем этажам — для карточки и «Подробнее». */
function tenantsInside(v: Venue, unit: Venue['units'][number]): FloorItem[] {
  const floors = Math.max(1, buildingFloors(v, unit))
  const out: FloorItem[] = []
  for (let floor = 1; floor <= floors; floor++) {
    out.push(...floorOccupants(v, unit.id, floor).tenants)
  }
  return out
}

/**
 * Арендаторы здания на этаже n. Раньше здесь смотрели только POI и только
 * `attrs.floor`, из-за чего на импортированном ТЦ этажерка показывала «2, —, —»
 * вместо сотни магазинов (фидбек владельца 28.07). Теперь — общий счётчик.
 */
export function tenantsOnFloor(v: Venue, unitId: string, n: number): FloorItem[] {
  return [...floorOccupants(v, unitId, n).tenants]
}

function attrStr(poi: Poi | Unit, key: string): string {
  const v = poi.attrs?.[key]
  return typeof v === 'string' || typeof v === 'number' ? String(v) : ''
}

/** «Как найти внутри»: ориентир из редактора (авто или свой текст). */
function TenantHint({ poi }: { poi: Poi }) {
  const hint = attrStr(poi, 'navigation_hint')
  if (!hint) return null
  return (
    <div class="card-hint">
      <span
        class="card-hint-icon"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: GLYPH_COMPASS }}
      />
      <div>
        <p class="card-hint-title">{tr('card.how_to_find')}</p>
        <p class="card-hint-text">{hint}</p>
      </div>
    </div>
  )
}

/** Есть ли у цели раскрываемые детали (кнопка «Подробнее» без контента — обман). */
export function hasCardDetails(v: Venue, id: string): boolean {
  const unit = v.units.find((u) => u.id === id)
  if (unit) {
    const floors = Number(unit.attrs?.['floors']) || 0
    return floors > 0 || Boolean(attrStr(unit, 'code')) || tenantsInside(v, unit).length > 0
  }
  const poi = v.pois.find((p) => p.id === id)
  if (!poi) return false
  const tags = Array.isArray(poi.attrs?.['tags']) ? (poi.attrs['tags'] as string[]) : []
  return Boolean(
    unitContaining(v, poi) ||
      attrStr(poi, 'floor') ||
      attrStr(poi, 'unit_number') ||
      attrStr(poi, 'area_m2') ||
      attrStr(poi, 'navigation_hint') ||
      tags.length > 0,
  )
}

/** Детали арендатора: здание · этаж · помещение · площадь + теги + ориентир. */
export function TenantDetails({ id }: { id: string }) {
  const v = venue.value
  if (!v) return null
  const poi = v.pois.find((p) => p.id === id)
  if (!poi) return null
  const host = unitContaining(v, poi)
  const floor = attrStr(poi, 'floor')
  const unitNumber = attrStr(poi, 'unit_number')
  const area = attrStr(poi, 'area_m2')
  const tags = Array.isArray(poi.attrs?.['tags']) ? (poi.attrs['tags'] as string[]) : []
  const facts = [
    host ? featureName(v, host.id) : '',
    floor ? tr('card.floor_n', { n: floor }) : '',
    unitNumber,
    area ? `${area} ${tr('unit.m2')}` : '',
  ].filter(Boolean)
  return (
    <div class="card-details">
      {facts.length > 0 && <p class="card-facts">{facts.join(' · ')}</p>}
      {tags.length > 0 && (
        <div class="card-tags">
          {tags.slice(0, 6).map((t) => (
            <span key={t} class="card-tag">
              {t}
            </span>
          ))}
        </div>
      )}
      <TenantHint poi={poi} />
    </div>
  )
}

/** Детали здания: этажность/код + арендаторы по этажам (клик — карточка). */
export function BuildingDetails({ id }: { id: string }) {
  const v = venue.value
  if (!v) return null
  const unit = v.units.find((u) => u.id === id)
  if (!unit) return null
  const floors = Number(unit.attrs?.['floors']) || 0
  const code = attrStr(unit, 'code')
  const tenants = tenantsInside(v, unit)
  const facts = [
    floors > 0 ? tr('card.floors_n', { n: String(floors) }) : '',
    code,
    tenants.length > 0 ? tr('card.tenants_n', { n: String(tenants.length) }) : '',
  ].filter(Boolean)
  return (
    <div class="card-details">
      {facts.length > 0 && <p class="card-facts">{facts.join(' · ')}</p>}
      {floors > 1 && (
        <button
          type="button"
          class="btn-secondary fs-open-btn"
          onClick={() => {
            floorStackFloor.value = 1
            floorStackUnitId.value = id
          }}
        >
          ⬚ {tr('floorstack.open', { n: String(floors) })}
        </button>
      )}
      {floors > 1 && v?.venue.volume3d !== false && volumeTier() !== 'off' && (
        <button
          type="button"
          class="btn-secondary fs-open-btn"
          // Навёл — начинаем тянуть сцену: к клику она уже в кэше, а посетители,
          // которым 3D не нужен, за него не платят.
          onPointerEnter={prefetchVolume3d}
          onFocus={prefetchVolume3d}
          onClick={() => {
            volume3dUnitId.value = id
          }}
        >
          ◈ {tr('volume3d.open')}
        </button>
      )}
      {tenants.length > 0 && (
        <div class="card-tenants">
          <p class="card-section-title">{tr('card.tenants_title')}</p>
          {tenants.map((p) => {
            const { svg, color } = markerVisual(p.category)
            const floor = floorLabelOf(v, p)
            return (
              <button
                key={p.id}
                type="button"
                class="card-tenant-row"
                onClick={() => selectTarget(p.id)}
              >
                <span
                  class="card-tenant-icon"
                  style={{ '--mk': color }}
                  dangerouslySetInnerHTML={{ __html: svg }}
                />
                <span class="card-tenant-name">{featureName(v, p.id)}</span>
                <span class="card-tenant-sub">
                  {categoryLabel(p.category)}
                  {floor ? ` · ${tr('card.floor_n', { n: floor })}` : ''}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** «✎ Редактировать» — только при живой сессии кабинета с правами
 *  на площадку (владелец/админ); анонимный посетитель ссылки не видит. */
export function EditInCabinetLink() {
  const v = venue.value
  if (!v || !canEditVenue.value) return null
  return (
    <a class="card-edit-link" href={`/b2b/#/editor/${v.venue.id}`} target="_blank" rel="noopener">
      ✎ {tr('card.edit_link')}
    </a>
  )
}
